//-- Copyright (c) 2021 TMT Insights Group, LLC
import { Grid, Typography } from '@mui/material';
import { JobDetail, OrderJob } from '@/redux/slices/slice_types';
import { StatusColors } from '@/constants/constants';
import { findandFormatUTCDateInString } from '@/_utils/date';
import { JobRequirements } from '@/components/_common/JobRequirements';
import TitleHierarchy from '@/components/_common/TitleHierarchy';
import {
  ClickCopy,
  DateTimeDisplay,
  JobsEmbargoDate,
  JobsOverflowMenu,
  JobTypeLabel,
  Notes,
  TableJobStage,
  UserDisplay
} from '@/exports/columnCommonComponents';

export default {
  hid: {
    label: 'Job ID',
    sortField: 'id',
    minWidth: 75,
    render: (cell: JobDetail['hid']) => (
      <ClickCopy copyText={cell} noBackground disableTextClick>
        <Typography variant="subtitle1">{cell}</Typography>
      </ClickCopy>
    )
  },
  job_type: {
    label: 'Job Type',
    render: (cell: JobDetail['job_type']) => <JobTypeLabel type={cell} />
  },
  description: {
    label: 'Title',
    clickCopy: true,
    minWidth: 250,
    render: (_cell: any, job: OrderJob) => <TitleHierarchy title={job.title} nameFallback={job.description} />
  },
  category: {
    label: 'Category',
    minWidth: 200
  },
  version__name: {
    label: 'Version',
    minWidth: 200
  },
  output_edit_type: {
    label: 'Output Master Type',
    sortField: 'validated_workflow_inputs__edit_type'
  },
  order__managed_by: {
    label: 'Managed By',
    disableSort: true,
    minWidth: 100,
    render: (cell: JobDetail['order']['managed_by'], job: OrderJob) =>
      cell ? <UserDisplay user={cell} emptyText="-" /> : job.order?.managed_by || '-'
  },
  customer: {
    label: 'Customer',
    clickCopy: true,
    minWidth: 180
  },
  profile__name: {
    label: 'Profile',
    clickCopy: true,
    sortField: 'job_profile',
    minWidth: 240,
    render: (cell: JobDetail['profile']) => findandFormatUTCDateInString(cell?.name)
  },
  due_date: {
    label: 'Due Date',
    clickCopy: true,
    width: 100,
    render: (cell: JobDetail['due_date']) => <DateTimeDisplay>{cell}</DateTimeDisplay>
  },
  created_at: {
    label: 'Created',
    clickCopy: true,
    width: 100,
    render: (cell: JobDetail['created_at']) => <DateTimeDisplay>{cell}</DateTimeDisplay>
  },
  completed_at: {
    label: 'Completed',
    minWidth: 100,
    render: (cell: JobDetail['completed_at']) => <DateTimeDisplay>{cell}</DateTimeDisplay>
  },
  stage: {
    label: 'Status',
    render: (cell: JobDetail['stage'], row: JobDetail) => (
      <Grid container rowSpacing={1}>
        <Grid item xs={12}>
          <TableJobStage
            status={row.status === 'cancelled' ? row.status : cell || row.status}
            colorOverride={StatusColors[row.status as keyof typeof StatusColors]}
          />
        </Grid>
        {row?.embargo_date && !row?.embargo_resumed_at && (
          <Grid item xs={12}>
            <JobsEmbargoDate job={row} />
          </Grid>
        )}
      </Grid>
    )
  },
  assets: {
    label: 'Requirements',
    render: (cell: JobDetail['assets'], row: JobDetail) => (
      <div style={{ opacity: row.status === 'cancelled' ? '0.5' : '1', marginLeft: -15 }}>
        <JobRequirements sources={cell} />
      </div>
    ),
    tooltip: {
      title: 'Displays sources received out of the total expected',
      placement: 'top'
    }
  },
  notes: {
    label: 'Notes',
    render: (_cell: any, row: JobDetail) => (
      <div style={{ width: 40, textAlign: 'center' }}>
        <Notes data={row} type="jobs" />
      </div>
    ),
    disableSort: true
  },
  overflow: {
    label: '',
    isOverflow: true,
    render: (_cell: any, job: OrderJob) => <JobsOverflowMenu row={job} />
  }
};
